import keys from '@debbie/common/dist/cache/keys.js';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { Entity, addressToString } from '@debbie/common/dist/index.js';
import { IntentType } from '@debbie/common/dist/intents/index.js';
import { EBoksRecipientType } from '@debbie/common/dist/interactions/eboks.js';
import { findRecipient, genRecipientsRaw, } from '@debbie/common/dist/interactions/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { toEditorMentions } from '@debbie/common/dist/templates/suggestions.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { patchUser } from '@debbie/cortex/dist/api/customers.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { customersEntity, useCustomer, } from '@debbie/cortex/dist/customers/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useNewInteraction } from '@debbie/cortex/dist/newInteractions/index.js';
import { eboksInitialState } from '@debbie/cortex/dist/newInteractions/types.js';
import q from '@debbie/cortex/dist/query.js';
import { useMentionSuggestions } from '@debbie/cortex/dist/templates/index.js';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSubjectContent, parseText } from '../../utils/templates.js';
import { File, FileDropzone, Input, Intent } from '..//index.js';
import Disclaimer from '../Disclaimer.js';
import NewEditor from '../editor/Editor.js';
import { isEditorEmpty, useEditorState } from '../editor/hooks.js';
import { htmlToPlainText } from '../editor/utils.js';
import X from '../Icons/X.js';
import AddFromTemplate from './AddFromTemplate.js';
import InteractionFooter, { AddDebtorDocumentsAction, AddIntentAction, AddTemplateAction, AttachAction, PreviewAction, SendButton, } from './InteractionFooter.js';
import { PDFTemplatePreviewModal } from './PDFTemplatePreview.js';
import TemplateSearch from './TemplateSearch.js';
import VisibilityToggle from './VisibilityToggle.js';
const EBoksInteraction = (props) => {
    const lang = useSelector((s) => s.lang);
    const [interaction, setInteraction] = useNewInteraction(props.referenceId);
    const eboks = interaction.eboks;
    const uploadedFiles = interaction.uploadedFiles;
    const customerLoader = useCustomer(props.case?.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const dropzoneRef = useRef(null);
    const [eboksPreviewModalOpen, setEBoksPreviewModalOpen] = useState(false);
    const [emailTemplateAdderOpen, setEmailTemplateAdderOpen] = useState(false);
    const [visibleToClients, setVisibleToClients] = useState(true);
    const subjectState = useEditorState(eboks.subject);
    const bodyState = useEditorState(eboks.content);
    const creditorLoadable = useCreditor(props.case?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const documentsLoadable = useDocumentsByReference(props.referenceType === Entity.Case ? props.referenceId : undefined);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const mentionSuggestions = useMentionSuggestions(TemplateType.EBOKS, props.case?.caseGroup?.id || null);
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const contactPerson = getContactPerson(props.customer, props.case);
    const toggleEBoksPreviewModal = () => {
        setEBoksPreviewModalOpen(!eboksPreviewModalOpen);
    };
    const toggleAddTemplateModal = () => {
        setEmailTemplateAdderOpen(!emailTemplateAdderOpen);
    };
    const hasBody = () => !isEditorEmpty(bodyState.html);
    const recipients = genRecipientsRaw(props.customer, props.case, {
        addressId: props.addressId,
    });
    let recipient = findRecipient(recipients, props.customer, props.case);
    const unlikelyToReceive = recipients.length > 0 && recipient == null;
    if (recipient == null && recipients.length > 0) {
        recipient = recipients[0];
    }
    const sendEBoksMutation = useMutation(async () => {
        if (!collector) {
            throw new Error('Could not find collector');
        }
        const pdfBody = parseText(bodyState.html, mentionSuggestions);
        const eBoksTitle = getSubjectContent(htmlToPlainText(parseText(subjectState.html, mentionSuggestions)));
        if (!recipient) {
            throw new Error('No recipient');
        }
        await c.newInteractions.sendEBoks(props.referenceId, Entity.Case, eboks.templateId, pdfBody, eBoksTitle, (uploadedFiles || []).map((f) => f.fileId), visibleToClients ? 'client' : 'caseworker', eboks.intent, recipient);
        subjectState.overwriteEditorContent(eboksInitialState.subject);
        bodyState.overwriteEditorContent(eboksInitialState.content);
    });
    const sendEBoks = () => {
        sendEBoksMutation.mutate();
    };
    const deliverabilityUpdater = useMutation(() => {
        const contact = getContactPerson(props.customer, props.case);
        if (contact == null) {
            throw new Error('No contact person');
        }
        const deliverability = contact.details.deliverability ?? {};
        const eboks = deliverability.eboks ?? [];
        const newEboks = eboks.concat(recipients.map((r) => {
            return {
                timestamp: new Date().toISOString(),
                recipient: r,
                success: true,
                manualOverride: true,
            };
        }));
        patchUser(props.customer.customerId, contact.userId, {
            details: {
                deliverability: { ...deliverability, eboks: newEboks },
            },
        });
    }, {
        onSuccess: () => {
            q.invalidate(keys.write.modify(customersEntity, props.customer.customerId));
        },
    });
    const documentsVisibleForDebtorToAdd = documents
        ? documents.filter((document) => document.minAccessRole === 'debtor' &&
            (document.signedFile || document.file)?.contentType ===
                'application/pdf' &&
            !uploadedFiles.find((uploadedFile) => uploadedFile.fileId === document.file?.fileId))
        : null;
    return (React.createElement(FileDropzone, { onChange: (file) => {
            setInteraction('uploadedFiles', [...uploadedFiles, file]);
        }, dropzoneRef: dropzoneRef, accept: "application/pdf" },
        unlikelyToReceive ? (React.createElement(Disclaimer, null,
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                } },
                lang.interactions.unlikelyToReceive,
                React.createElement(X, { "data-tooltip-id": "tooltip-default", "data-tooltip-content": lang.interactions.removeMarking, style: { cursor: 'pointer' }, onClick: () => {
                        deliverabilityUpdater.mutate();
                    } })))) : null,
        React.createElement(TemplateSearch, { interaction: eboks, templateType: TemplateType.EBOKS, case: props.case, customer: props.customer, onChange: (state, subject) => {
                bodyState.overwriteEditorContent(state);
                if (subject != null) {
                    subjectState.overwriteEditorContent(subject);
                }
            } }),
        React.createElement(Input, { disabled: true, placeholder: lang.interactions.contact, value: recipient
                ? recipient.type === EBoksRecipientType.AddressId ||
                    recipient.type === EBoksRecipientType.KVHX
                    ? `${recipient.firstName} ${recipient.lastName} (${contactPerson && contactPerson.details.address
                        ? addressToString(contactPerson.details.address, {
                            noCoAddress: true,
                            noCountry: true,
                        })
                        : ''})`
                    : recipient.type === EBoksRecipientType.CentralRegisterId
                        ? `${recipient.isCompany ? 'CVR' : 'CPR'}: ${recipient.centralRegisterId}`
                        : ''
                : '' }),
        React.createElement(NewEditor, { hideControls: true, compact: true, singleLine: true, disabled: !eboks.templateId, placeholder: lang.interactions.subject, initialContent: subjectState.editorContent, mentions: toEditorMentions(mentionSuggestions), onChange: (html) => {
                subjectState.onChange(html);
                setInteraction('eboks', { ...eboks, subject: html });
            } }),
        React.createElement("br", null),
        React.createElement(NewEditor, { mentions: toEditorMentions(mentionSuggestions), disabled: !eboks.templateId, mentionSuggestions: mentionSuggestions, initialContent: bodyState.editorContent, onChange: (html) => {
                bodyState.onChange(html);
                setInteraction('eboks', { ...eboks, content: html });
            } }),
        React.createElement("br", null),
        React.createElement("div", { style: { marginTop: '20px' } },
            !!(uploadedFiles || [])?.length &&
                (uploadedFiles || []).map((file, i) => (React.createElement(File, { key: file.fileId, file: file, onRemove: () => {
                        setInteraction('uploadedFiles', [
                            ...uploadedFiles.slice(0, i),
                            ...uploadedFiles.slice(i + 1),
                        ]);
                    }, removable: true }))),
            emailTemplateAdderOpen &&
                props.referenceType === Entity.Case &&
                props.case &&
                customer && (React.createElement(AddFromTemplate, { toggleAddTemplateModal: toggleAddTemplateModal, case: props.case, customer: customer, mentionSuggestions: mentionSuggestions }))),
        props.isDebtor && eboks.intent.type !== IntentType.Unknown && (React.createElement("div", { style: { margin: '1em 0 0 0' } },
            React.createElement(Intent, { intent: eboks.intent, editable: true, onEdit: (intent) => {
                    setInteraction('eboks', { ...eboks, intent: intent });
                } }))),
        React.createElement(InteractionFooter, { error: sendEBoksMutation.error, primary: React.createElement(React.Fragment, null,
                React.createElement(SendButton, { loading: eboks.loading || sendEBoksMutation.isLoading, disabled: !(eboks.templateId && recipient) || !hasBody(), onClick: sendEBoks }, lang.interactions.send),
                React.createElement(PreviewAction, { onClick: toggleEBoksPreviewModal, disabled: !eboks.templateId || !hasBody() })), actions: React.createElement(React.Fragment, null,
                React.createElement(AttachAction, { onClick: () => dropzoneRef.current?.open() }),
                React.createElement(AddTemplateAction, { onClick: toggleAddTemplateModal }),
                React.createElement(AddDebtorDocumentsAction, { documents: documentsVisibleForDebtorToAdd, onAdd: (files) => setInteraction('uploadedFiles', [...uploadedFiles, ...files]) }),
                props.isDebtor && eboks.intent.type === IntentType.Unknown && (React.createElement(AddIntentAction, { onAdd: (intent) => setInteraction('eboks', { ...eboks, intent }) })),
                React.createElement(VisibilityToggle, { visible: visibleToClients, onChange: setVisibleToClients })) }),
        eboksPreviewModalOpen && (React.createElement(PDFTemplatePreviewModal, { onToggle: toggleEBoksPreviewModal, caseId: props.case.caseId, templateId: eboks.templateId, body: parseText(bodyState.html, mentionSuggestions) }))));
};
export default EBoksInteraction;
//# sourceMappingURL=EBoksInteraction.js.map